import React, { useState, useRef, useLayoutEffect } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import PlusIconNarrow from '~/icons/components/PlusIconNarrow';
import { Link } from '~/techstyle-shared/next-routes';
import {
  SkeletonLine,
  useBreakpoint,
  mobile,
} from '~/techstyle-shared/react-components';
import useId from '~/techstyle-shared/react-components/src/useId.tsx';
import slugify from '~/techstyle-shared/react-components/src/utils/slugify';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import { useGridProducts } from '../../context/GridDataContexts';
import useNumGridCols from '../../hooks/useNumGridCols';
import { getSwatchTypeRBKey } from '../../utils/swatches';

const DynamicFormattedMessage = FormattedMessage;

const SwatchesWrapper = styled.span`
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  width: 100%;
  padding: 0 16px;
  position: relative;
  ${mobile`
    padding: 0 12px;
  `}
`;

const SwatchContainer = styled.div`
  height: 24px;
  width: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SwatchGroup = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  position: relative;
`;

const SwatchTouchpoint = styled.a`
  width: 24px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  position: absolute;
`;

const SwatchHover = styled.div`
  position: absolute;
  display: inline-flex;
  padding: 12px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.gridV2SwatchHoverBackground};
  box-shadow: 0 4px 20px 0 ${({ theme }) => theme.colors.gridV2SwatchBoxShadow};
  backdrop-filter: blur(5px);
  z-index: 6;
  overflow: hidden;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition:
    opacity 150ms ease,
    left 150ms ease,
    right 150ms ease,
    top 150ms ease;
`;

const SwatchHoverLabel = styled.div`
  ${({ theme, $isOutOfStock }) =>
    $isOutOfStock
      ? `color: ${theme.colors.gridV2SwatchHoverLabelOOS}`
      : `color: ${theme.colors.gridV2SwatchHoverLabel}`};
  leading-trim: both;
  text-edge: cap;
  font-family: ${({ theme }) => theme.fontStacks.standardGtPressuraFamily};
  font-size: ${({ $isHeader }) => ($isHeader ? '12px' : '14px')};
  font-style: normal;
  font-weight: ${({ $isHeader }) => ($isHeader ? '500' : '350')};
  ${({ $isHeader }) => $isHeader && 'text-transform: uppercase;'}
  white-space: nowrap;
`;

const SwatchCircle = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: transparent;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    pointer-events: none;
  }

  /* Inner white ring */
  &::before {
    top: 3px;
    left: 3px;
    right: 3px;
    bottom: 3px;
    background: ${({ $isActive }) => ($isActive ? 'white' : 'transparent')};
    z-index: 1;
  }

  /* Outer black ring */
  &::after {
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: ${({ $isActive, theme }) =>
      $isActive
        ? `1px solid ${theme.colors.gridProductCardSwatchBorderSelected}`
        : 'none'};
    z-index: 1;
  }

  width: 24px;
  height: 24px;
`;

const SwatchInnerCircle = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  z-index: 4;

  height: 100%;
  width: 100%;
  max-width: ${({ isActive }) => (isActive ? '16px' : '24px')};
  max-height: ${({ isActive }) => (isActive ? '16px' : '24px')};
  background-clip: padding-box;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: 0.5px solid ${({ theme }) => theme.colors.grid2SwatchBorder};
    border-radius: 50%;
    pointer-events: none;
    z-index: 4;
  }
`;

const SwatchPressedCircle = styled.div`
  height: 32px;
  width: 32px;
  border-radius: 50%;
  ${({ isPressed, theme }) =>
    isPressed && `border: 1px solid ${theme.colors.grid2Swatch};`}
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SwatchImage = styled.img`
  height: 100%;
  width: 100%;
  max-width: ${({ $isActive }) => ($isActive ? '16px' : '24px')};
  max-height: ${({ $isActive }) => ($isActive ? '16px' : '24px')};
  border: 0.5px solid ${({ theme }) => theme.colors.grid2SwatchBorder};

  border-radius: 50%;
  position: relative;
  z-index: 2;
  background-color: ${({ $isActive, theme }) =>
    $isActive
      ? theme.colors.gridProductCardSwatchBackgroundSelected
      : 'transparent'};
`;

const OutOfStockOverlay = styled.div`
  width: 3px;
  height: 34px;
  transform: rotate(33deg);
  position: absolute;
  z-index: 5;
  border: 1px solid ${({ theme }) => theme.colors.gridV2OutOfStockOverlay};
  background: ${({ theme }) => theme.colors.grid2SwatchOOSSlash};
  background-clip: padding-box;
  ${({ $isOutOfStock }) => !$isOutOfStock && 'display: none;'}
`;

const MoreSwatchesLink = styled.a`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.gridProductCardSwatchMoreSwatchesIcon};
  gap: 8px;
  height: 24px;
`;

const MoreSwatchesPlusNumber = styled.div`
  display: flex;
  align-items: baseline;
  gap: 2px;
`;

const MoreSwatchesDivider = styled.div`
  width: 1px;
  height: 24px;
  background: ${({ theme }) => theme.colors.grid2Swatch};
`;

const MoreSwatchesLabel = styled.div`
  position: relative;
  top: -1px;
  line-height: 1;
  color: ${({ theme }) => theme.colors.grid2SwatchLabel};
  font-family: ${({ theme }) => theme.fontStacks.standardGtPressuraFamily};
  font-size: 12px;
  font-style: normal;
  font-weight: 350;
`;

const StyledSkeleton = styled(SkeletonLine)`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid
    ${({ theme }) => theme.colors.gridProductCardSwatchSkeletonBorder};
`;

// Returns the max number of swatches that fit without overflow.
// Uses a fixed set of useBreakpoint calls (stable hook count) and selects
// the right value based on numGridCols.
const useMaxSwatchesToRender = (numGridCols) => {
  const sub372 = useBreakpoint('(max-width: 372px)').matches;
  const sub549 = useBreakpoint('(max-width: 549px)').matches;
  const sub700 = useBreakpoint('(max-width: 700px)').matches;
  const sub800 = useBreakpoint('(max-width: 800px)').matches;
  const sub900 = useBreakpoint('(max-width: 900px)').matches;
  const sub1170 = useBreakpoint('(max-width: 1170px)').matches;
  const sub1240 = useBreakpoint('(max-width: 1240px)').matches;

  if (numGridCols === 2) {
    return sub372 ? 3 : 4;
  }

  if (numGridCols >= 4) {
    if (sub1170) {
      return 5;
    }
    if (sub1240) {
      return 6;
    }
    return 7;
  }

  // 3-column grid
  if (sub549) {
    return 3;
  }
  if (sub700) {
    return 4;
  }
  if (sub800) {
    return 5;
  }
  if (sub900) {
    return 6;
  }
  return 7;
};

const DEFAULT_FUNCTION = () => {};
const DEFAULT_SWATCHES = [];

const ProductCardSwatches = ({
  swatches = DEFAULT_SWATCHES,
  activeProductId,
  activeProductPdpLink,
  setActiveProductId = DEFAULT_FUNCTION,
  trackProductSwatchClick = DEFAULT_FUNCTION,
  totalProducts,
}) => {
  const { isMobile } = useBreakpoint();

  const [hoveredProductId, setHoveredProductId] = useState(null);
  const [pressedProductId, setPressedProductId] = useState(null);
  const { isRelatedProductsLoading, isRelatedProductsFetching } =
    useGridProducts();
  const numGridCols = useNumGridCols();
  const maxSwatchesToRender = useMaxSwatchesToRender(numGridCols);
  const elementId = useId();

  const swatchesWrapperRef = useRef(null);
  const swatchTouchpointRefs = useRef({});
  const hoverRef = useRef(null);
  const [hoverPosition, setHoverPosition] = useState(null);
  const lastHoveredSwatchRef = useRef(null); // so swatch hover persists during fade out

  useLayoutEffect(() => {
    if (!hoveredProductId || isMobile) {
      return;
    }

    const wrapperEl = swatchesWrapperRef.current;
    const touchpointEl = swatchTouchpointRefs.current[hoveredProductId];
    const hoverEl = hoverRef.current;

    if (!wrapperEl || !touchpointEl || !hoverEl) {
      return;
    }

    const wrapperRect = wrapperEl.getBoundingClientRect();
    const touchpointRect = touchpointEl.getBoundingClientRect();
    const wrapperWidth = wrapperRect.width;

    // All positions relative to SwatchesWrapper
    const swatchCenterX =
      (touchpointRect.left + touchpointRect.right) / 2 - wrapperRect.left;
    const top = touchpointRect.bottom - wrapperRect.top;

    // Temporarily remove positioning constraints to measure natural content width
    const savedStyle = hoverEl.style.cssText;
    Object.assign(hoverEl.style, {
      maxWidth: 'none',
      whiteSpace: 'nowrap',
      left: '0',
      right: 'auto',
    });
    const naturalWidth = hoverEl.getBoundingClientRect().width;
    hoverEl.style.cssText = savedStyle;

    // Center on swatch, then clamp so hover stays within wrapper
    const left = Math.max(
      0,
      Math.min(swatchCenterX - naturalWidth / 2, wrapperWidth - naturalWidth)
    );
    const right = Math.max(0, wrapperWidth - left - naturalWidth);

    setHoverPosition({ left, right, top, maxWidth: wrapperWidth });
  }, [hoveredProductId, isMobile]);

  const isActive = (productId) => productId === activeProductId;
  const hoveredSwatch = swatches?.find(
    (s) => s?.productId === hoveredProductId
  );
  const isHoverVisible = !isMobile && !!hoveredSwatch;

  if (hoveredSwatch) {
    lastHoveredSwatchRef.current = hoveredSwatch;
  }
  const displayedSwatch = hoveredSwatch || lastHoveredSwatchRef.current;

  // See ProductCardSwatches.md for details on this loading logic
  if (
    isRelatedProductsLoading ||
    (swatches?.length <= 1 && isRelatedProductsFetching)
  ) {
    return (
      <SwatchesWrapper>
        {Array.from({ length: maxSwatchesToRender + 1 }, (_, index) => (
          <StyledSkeleton key={index} />
        ))}
      </SwatchesWrapper>
    );
  }

  return (
    <SwatchesWrapper
      ref={swatchesWrapperRef}
      data-testid="grid-product-swatches"
    >
      <SwatchGroup>
        {swatches?.slice(0, maxSwatchesToRender).map((swatch) => {
          return (
            <SwatchContainer key={swatch?.productId}>
              <SwatchTouchpoint
                ref={(el) => {
                  if (el) {
                    swatchTouchpointRefs.current[swatch?.productId] = el;
                  } else {
                    delete swatchTouchpointRefs.current[swatch?.productId];
                  }
                }}
                onPointerEnter={() => {
                  setHoveredProductId(swatch?.productId);
                }}
                onPointerLeave={() => {
                  setHoveredProductId(null);
                  setPressedProductId(null);
                }}
                onPointerDown={() => {
                  setPressedProductId(swatch?.productId);
                  trackProductSwatchClick(swatch?.productId);
                  setActiveProductId(swatch?.productId);
                }}
                onPointerUp={() => setPressedProductId(null)}
                onFocus={() => setHoveredProductId(swatch.productId)}
                onBlur={() => setHoveredProductId(null)}
                data-masterproductid={swatch?.productId}
                data-autotag={`${slugify(swatch?.label, '-')}-${elementId}`}
              >
                <SwatchPressedCircle
                  isPressed={pressedProductId === swatch?.productId}
                >
                  <SwatchCircle
                    $isActive={isActive(swatch?.productId)}
                    isHovered={hoveredProductId === swatch?.productId}
                  >
                    <SwatchInnerCircle isActive={isActive(swatch?.productId)}>
                      <OutOfStockOverlay $isOutOfStock={swatch?.isOutOfStock} />
                      <SwatchImage
                        $isActive={isActive(swatch?.productId)}
                        src={swatch?.url}
                        draggable="false"
                        onError={(e) => {
                          e.target.onerror = null; // prevents looping
                          e.target.style.display = 'none'; // hide the image
                        }}
                      />
                    </SwatchInnerCircle>
                  </SwatchCircle>
                </SwatchPressedCircle>
              </SwatchTouchpoint>
            </SwatchContainer>
          );
        })}
      </SwatchGroup>

      {!isMobile && (
        <SwatchHover
          ref={hoverRef}
          $visible={isHoverVisible}
          style={
            hoverPosition
              ? {
                  left: hoverPosition.left,
                  right: hoverPosition.right,
                  top: hoverPosition.top,
                  maxWidth: hoverPosition.maxWidth,
                }
              : { visibility: 'hidden' }
          }
        >
          {displayedSwatch?.type && (
            <SwatchHoverLabel
              $isOutOfStock={displayedSwatch?.isOutOfStock}
              $isHeader
            >
              {displayedSwatch?.isOutOfStock ? (
                <FormattedMessage
                  id="grid_static.callout_out_of_stock"
                  defaultMessage="Out of Stock"
                />
              ) : (
                <DynamicFormattedMessage
                  id={`grid_dynamic.${getSwatchTypeRBKey(
                    displayedSwatch?.type
                  )}`}
                  defaultMessage={displayedSwatch?.type}
                />
              )}
            </SwatchHoverLabel>
          )}
          <SwatchHoverLabel>{displayedSwatch?.label}</SwatchHoverLabel>
        </SwatchHover>
      )}

      {totalProducts > maxSwatchesToRender && (
        <Link href={activeProductPdpLink}>
          <MoreSwatchesLink>
            <MoreSwatchesDivider />
            <MoreSwatchesPlusNumber>
              <PlusIconNarrow />
              <MoreSwatchesLabel>
                {totalProducts - maxSwatchesToRender}
              </MoreSwatchesLabel>
            </MoreSwatchesPlusNumber>
          </MoreSwatchesLink>
        </Link>
      )}
    </SwatchesWrapper>
  );
};

ProductCardSwatches.propTypes = {
  activeProductId: PropTypes.number,
  activeProductPdpLink: PropTypes.string,
  setActiveProductId: PropTypes.func,
  swatches: PropTypes.arrayOf(
    PropTypes.shape({
      productId: PropTypes.number.isRequired,
      url: PropTypes.string.isRequired,
      label: PropTypes.string,
    })
  ),
  totalProducts: PropTypes.number,
  trackProductSwatchClick: PropTypes.func,
};

export default ProductCardSwatches;
